import {
	type CognitiveLevel,
	type DifficultyLevel,
	type ExamAttemptSummary,
	ExamAttemptSummaryAction,
	type ExamType,
	type NccaExamCoachSummary,
	type Prisma,
	type QuestionType,
} from "@brookbush/database";

import z, { nullable } from "zod";

import type { PassedCertificationExpanded } from "./certifications.types";
import type { CoursePreview } from "./courses.types";

type PromptChild = {
	_type: string;
	marks: Array<any>;
	text: string;
	_key: string;
};

export type Prompt = {
	_type: string;
	style: string;
	_key: string;
	markDefs: Array<any>;
	children: Array<PromptChild>;
};

type QuestionBase = {
	_key: string;
	correctOption: string;
	otherOptions: Array<string>;
	prompt: Array<Prompt>;
	_type: string;
	course?: CoursePreview;
	rationale?: string;
};

type ImageQuestion = QuestionBase & {
	isImageQuestion: true;
	correctImageOption: Array<Prompt>;
	otherImageOptions: Array<Prompt>[];
};

type TextQuestion = QuestionBase & {
	isImageQuestion: false;
};

export type Question = ImageQuestion | TextQuestion;

export type PracticeQuestionKeys = {
	_key: string;
};

export type ExamQuestionKeys = {
	_key: string;
	_pairKey: string;
};

export type PracticeQuestion = Question & PracticeQuestionKeys;

export type ExamQuestion = Question & ExamQuestionKeys;

type _QuestionCore = {
	answer: string;
	distractors: Array<string>;
	stem: string;
};

export type PracticeQuestionCore = _QuestionCore & PracticeQuestionKeys;

export type ExamQuestionCore = _QuestionCore & ExamQuestionKeys;

export type QuestionCore = PracticeQuestionCore | ExamQuestionCore;

type _QuestionWithMetadata = {
	answer: string;
	cognitiveLevel: string;
	courseId: string;
	difficultyLevel: string;
	distractors: Array<string>;
	multiLogicExplanation?: Array<string> | null;
	questionType: string;
	rationale: string;
	section: string;
	stem: string;
	subSection?: string | null;
	tags: Array<string>;
};

export type PracticeQuestionWithMetadata = _QuestionWithMetadata &
	PracticeQuestionKeys;

export type ExamQuestionWithMetadata = _QuestionWithMetadata & ExamQuestionKeys;

export type QuestionWithMetadata =
	| PracticeQuestionWithMetadata
	| ExamQuestionWithMetadata;

export type QuestionMetadata = {
	key: string;
	courseId: string;
	questionType: QuestionType;
	difficultyLevel: DifficultyLevel;
	cognitiveLevel: CognitiveLevel;
	tags: Array<string>;
	sectionKey?: string | null;
	subSectionKey?: string | null;
};

interface ExamAttempt extends ExamsFormSchema {
	_type: ExamType;
	status: ExamStatus;
	isPassed?: boolean;
	totalCorrectAnswers?: number;
	totalQuestions?: number;
}

export interface PracticeExamAttempt extends ExamAttempt {
	_type: "PRACTICE";
	id?: number;
}

export type ExamAttemptQuestion = ExamsFormSchema["questions"][number];

export interface PracticeExam {
	courseId: string;
	practiceExam: Array<
		QuestionBase & {
			isImageQuestion: boolean;
			correctImageOption: Array<Prompt>;
			otherImageOptions: Array<Prompt>[];
			sectionKey?: string;
			subSectionKey?: string;
		}
	>;
}

export interface FinalExam {
	courseId: string;
	finalExam: Array<{
		_key: string;
		questionPair: Array<Question>;
	}>;
}

export interface FinalExamAttempt extends ExamAttempt {
	_type: "FINAL";
	id: number;
	remainingAttempts: number;
	createdAt: string;
	expiresAt: string;
	passedCourseId?: number;
	passedCertification?: PassedCertificationExpanded;
	timeLeft: number;
}

export type ExamAttemptDB = Prisma.ExamAttemptGetPayload<{
	include: {
		questions: true;
	};
}>;

export type FocusSection = {
	priority: number;
	sectionKey: string;
	subSectionKey: string | null;
	sectionTitle: string;
	subSectionTitle: string | null;
	rationale: string;
};

export type ExamAttemptSummaryExpanded = Omit<
	ExamAttemptSummary,
	"focusSections"
> & {
	focusSections: FocusSection[] | null;
	difficultyLevel?: Record<string, number>;
	cognitiveLevel?: Record<string, number>;
};

export interface NCCAPracticeExam {
	certificationId: string;
	nccaPracticeExam: Array<{
		_key: string;
		relatedCourse?: CoursePreview;
		questionPair: Array<Question>;
	}>;
}

export interface NCCAPracticeExamAttempt extends ExamAttempt {
	_type: "NCCA_PRACTICE";
	id?: number;
	groupedQuestions?: Record<string, ExamAttemptQuestion[]>;
}

export const examsFormSchema = z.object({
	questions: z.array(
		z.object({
			prompt: z.unknown(),
			_key: z.string(),
			options: z
				.array(
					z.object({
						hash: z.string(),
						value: z
							.string()
							.or(z.array(z.custom<Prompt>()))
							.or(z.array(z.any())),
					}),
				)
				.optional(),
			selectedOptionHash: z.string(),
			correctOptionHash: z.string().optional(),
			isCorrect: z.boolean().optional(),
			isImageQuestion: z.boolean().optional().nullable(),
			correctImageOption: z.array(z.custom<Prompt>()).optional(),
			selectedImageOption: z.array(z.custom<Prompt>()).optional(),
			course: z
				.object({
					_id: z.string(),
					title: z.string(),
					slug: z.string(),
				})
				.optional(),
			imageOptions: z
				.array(
					z.object({
						hash: z.string(),
						value: z.custom<Prompt[]>(),
					}),
				)
				.optional(),
			section: z.string().optional(),
			sectionKey: z.string().optional(),
			subSection: z.string().optional(),
			subSectionKey: z.string().optional(),
			rationale: z.string().optional(),
		}),
	),
});

export enum ExamStatus {
	Incomplete = "incomplete",
	Complete = "complete",
}

export type ExamsFormSchema = z.infer<typeof examsFormSchema>;

export type PastAttempt = Prisma.ExamAttemptGetPayload<{
	include: {
		questions: true;
	};
}> & {
	score: number;
	totalQuestions: number;
	correctAnswers: number;
};

export enum ExamDifficulty {
	UNRATED = 0,
	VERY_EASY,
	EASY,
	MEDIUM,
	HARD,
	VERY_HARD,
}

export type ReviewSection = {
	title: string;
	weight: number;
	subSections: Record<
		string,
		{
			title: string;
			weight: number;
		}
	>;
};

export type ReviewSections = Record<string, ReviewSection>;

export type ExamSummary = z.infer<typeof ExamSummarySchema>;

export const ExamSummarySchema = z.object({
	internalAnalysis: z.string(),
	actionIntent: z.enum(ExamAttemptSummaryAction),
	attemptAnalysis: z.string(),
	studyRecommendation: z.string(),
	focusSections: z.array(
		z.object({
			sectionKey: z.string(),
			subSectionKey: nullable(z.string()),

			priority: z.number(),
			rationale: z.string(),
		}),
	),
});

export type FocusCourse = {
	courseId: string;
	courseSlug: string;
	courseTitle: string;
	scorePercent: number;
	rationale: string;
};

export type NccaExamCoachSummaryExpanded = Omit<
	NccaExamCoachSummary,
	"focusCourses"
> & {
	focusCourses: FocusCourse[] | null;
};

export type NccaExamCoachAnalysis = z.infer<typeof NccaExamCoachAnalysisSchema>;

export const NccaExamCoachAnalysisSchema = z.object({
	attemptAnalysis: z.string(),
	focusCourses: z.array(
		z.object({
			courseId: z.string(),
			rationale: z.string(),
		}),
	),
});
